// =============================================
// COMPONENTE: NODE EDITOR MODAL - MOBILE FIRST
// =============================================
// Arquivo: NodeEditorModal.tsx
// Descrição: Modal fullscreen para edição de nós (mobile-friendly)
// =============================================

import React, { useState, useEffect } from 'react';
import NodeActionsEditor from './NodeActionsEditor';
import { NodeActions } from './types/actions';
import ModalPortal from '../Common/ModalPortal';
import './NodeEditorModal.css';

interface NodeEditorModalProps {
  node: any;
  isOpen: boolean;
  onClose: () => void;
  onSave: (nodeId: string, data: any) => void;
  isMobile: boolean;
}

const NodeEditorModal: React.FC<NodeEditorModalProps> = ({
  node,
  isOpen,
  onClose,
  onSave,
  isMobile
}) => {
  const [formData, setFormData] = useState<any>({});
  const [activeTab, setActiveTab] = useState<'config' | 'actions'>('config');

  useEffect(() => {
    if (node) {
      // Inicializar formData com dados do node
      const data = { ...node.data };

      // Inicializar ações se não existirem
      if (!data.actions) {
        data.actions = {
          input: [],
          output: []
        };
      }

      setFormData(data);
    }
  }, [node]);

  if (!isOpen || !node) return null;

  const handleSave = () => {
    onSave(node.id, formData);
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleActionsChange = (actions: NodeActions) => {
    setFormData((prev: any) => ({ ...prev, actions }));
  };

  const renderFields = () => {
    switch (node.type) {
      case 'start':
      case 'end':
        return (
          <div className="form-group">
            <label htmlFor="label">Rótulo</label>
            <input
              type="text"
              id="label"
              value={formData.label || ''}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder="Nome do nó"
            />
          </div>
        );

      case 'message':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do nó"
              />
            </div>
            <div className="form-group">
              <label htmlFor="messageType">Tipo de Mensagem</label>
              <select
                id="messageType"
                value={formData.messageType || 'text'}
                onChange={(e) => handleChange('messageType', e.target.value)}
              >
                <option value="text">Texto</option>
                <option value="image">Imagem</option>
                <option value="video">Vídeo</option>
                <option value="audio">Áudio</option>
                <option value="location">Localização</option>
                <option value="buttons">Botões</option>
                <option value="list">Lista</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="message">Mensagem</label>
              <textarea
                id="message"
                value={formData.message || ''}
                onChange={(e) => handleChange('message', e.target.value)}
                placeholder="Digite a mensagem..."
                rows={5}
              />
              <small>Use variáveis com {'{'}{'{'} nome_variavel {'}'}{'}'}</small>
            </div>
          </>
        );

      case 'input':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do nó"
              />
            </div>
            <div className="form-group">
              <label htmlFor="inputType">Tipo de Entrada</label>
              <select
                id="inputType"
                value={formData.inputType || 'text'}
                onChange={(e) => handleChange('inputType', e.target.value)}
              >
                <option value="text">Texto</option>
                <option value="number">Número</option>
                <option value="image">Imagem</option>
                <option value="location">Localização</option>
                <option value="audio">Áudio</option>
                <option value="video">Vídeo</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="variableName">Nome da Variável</label>
              <input
                type="text"
                id="variableName"
                value={formData.variableName || ''}
                onChange={(e) => handleChange('variableName', e.target.value)}
                placeholder="ex: nome_motorista"
              />
              <small>Nome da variável para armazenar o valor</small>
            </div>
          </>
        );

      case 'condition':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do nó"
              />
            </div>
            <div className="form-group">
              <label htmlFor="condition">Condição</label>
              <input
                type="text"
                id="condition"
                value={formData.condition || ''}
                onChange={(e) => handleChange('condition', e.target.value)}
                placeholder="ex: tag_veiculo != null"
              />
              <small>Use operadores: ==, !=, {'>'}, {'<'}, contains, etc</small>
            </div>
          </>
        );

      case 'action':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do nó"
              />
            </div>
            <div className="form-group">
              <label htmlFor="actionType">Tipo de Ação</label>
              <select
                id="actionType"
                value={formData.actionType || 'set_variable'}
                onChange={(e) => handleChange('actionType', e.target.value)}
              >
                <optgroup label="Variáveis">
                  <option value="set_variable">Definir Variável</option>
                  <option value="increment">Incrementar</option>
                  <option value="decrement">Decrementar</option>
                  <option value="conditional_set">Definir Condicional</option>
                </optgroup>
                <optgroup label="Transformações">
                  <option value="math">Expressão Matemática</option>
                  <option value="format_date">Formatar Data</option>
                  <option value="format_currency">Formatar Moeda</option>
                  <option value="string_transform">Transformar Texto</option>
                </optgroup>
                <optgroup label="Dados">
                  <option value="array_operation">Operação em Array</option>
                  <option value="json_extract">Extrair JSON</option>
                </optgroup>
                <optgroup label="Debug">
                  <option value="log">Log/Debug</option>
                </optgroup>
              </select>
            </div>

            {/* Set Variable */}
            {formData.actionType === 'set_variable' && (
              <>
                <div className="form-group">
                  <label htmlFor="variable">Nome da Variável</label>
                  <input
                    type="text"
                    id="variable"
                    value={formData.variable || ''}
                    onChange={(e) => handleChange('variable', e.target.value)}
                    placeholder="minha_variavel"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="value">Valor</label>
                  <input
                    type="text"
                    id="value"
                    value={formData.value || ''}
                    onChange={(e) => handleChange('value', e.target.value)}
                    placeholder="valor ou {{outra_variavel}}"
                  />
                  <small>Use {'{{variavel}}'} para referenciar outras variáveis</small>
                </div>
              </>
            )}

            {/* Increment */}
            {formData.actionType === 'increment' && (
              <>
                <div className="form-group">
                  <label htmlFor="variable">Nome da Variável</label>
                  <input
                    type="text"
                    id="variable"
                    value={formData.variable || ''}
                    onChange={(e) => handleChange('variable', e.target.value)}
                    placeholder="contador"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="amount">Quantidade</label>
                  <input
                    type="number"
                    id="amount"
                    value={formData.amount || 1}
                    onChange={(e) => handleChange('amount', parseInt(e.target.value) || 1)}
                    placeholder="1"
                  />
                  <small>Valor a ser adicionado (padrão: 1)</small>
                </div>
              </>
            )}

            {/* Decrement */}
            {formData.actionType === 'decrement' && (
              <>
                <div className="form-group">
                  <label htmlFor="variable">Nome da Variável</label>
                  <input
                    type="text"
                    id="variable"
                    value={formData.variable || ''}
                    onChange={(e) => handleChange('variable', e.target.value)}
                    placeholder="contador"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="amount">Quantidade</label>
                  <input
                    type="number"
                    id="amount"
                    value={formData.amount || 1}
                    onChange={(e) => handleChange('amount', parseInt(e.target.value) || 1)}
                    placeholder="1"
                  />
                  <small>Valor a ser subtraído (padrão: 1)</small>
                </div>
              </>
            )}

            {/* Math Expression */}
            {formData.actionType === 'math' && (
              <>
                <div className="form-group">
                  <label htmlFor="expression">Expressão Matemática</label>
                  <input
                    type="text"
                    id="expression"
                    value={formData.expression || ''}
                    onChange={(e) => handleChange('expression', e.target.value)}
                    placeholder="{{valor1}} + {{valor2}} * 2"
                  />
                  <small>Operadores: +, -, *, /, %, ** (potência)</small>
                </div>
                <div className="form-group">
                  <label htmlFor="variable">Variável de Resultado</label>
                  <input
                    type="text"
                    id="variable"
                    value={formData.variable || ''}
                    onChange={(e) => handleChange('variable', e.target.value)}
                    placeholder="resultado"
                  />
                </div>
              </>
            )}

            {/* Format Date */}
            {formData.actionType === 'format_date' && (
              <>
                <div className="form-group">
                  <label htmlFor="sourceVariable">Variável de Origem</label>
                  <input
                    type="text"
                    id="sourceVariable"
                    value={formData.sourceVariable || ''}
                    onChange={(e) => handleChange('sourceVariable', e.target.value)}
                    placeholder="data_original"
                  />
                  <small>Deixe vazio para usar data/hora atual</small>
                </div>
                <div className="form-group">
                  <label htmlFor="format">Formato</label>
                  <select
                    id="format"
                    value={formData.format || 'DD/MM/YYYY'}
                    onChange={(e) => handleChange('format', e.target.value)}
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY (01/01/2024)</option>
                    <option value="DD/MM/YYYY HH:mm">DD/MM/YYYY HH:mm (01/01/2024 14:30)</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD (2024-01-01)</option>
                    <option value="HH:mm">HH:mm (14:30)</option>
                    <option value="DD/MM">DD/MM (01/01)</option>
                    <option value="MMMM YYYY">MMMM YYYY (Janeiro 2024)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="variable">Variável de Resultado</label>
                  <input
                    type="text"
                    id="variable"
                    value={formData.variable || ''}
                    onChange={(e) => handleChange('variable', e.target.value)}
                    placeholder="data_formatada"
                  />
                </div>
              </>
            )}

            {/* Format Currency */}
            {formData.actionType === 'format_currency' && (
              <>
                <div className="form-group">
                  <label htmlFor="sourceVariable">Variável de Origem</label>
                  <input
                    type="text"
                    id="sourceVariable"
                    value={formData.sourceVariable || ''}
                    onChange={(e) => handleChange('sourceVariable', e.target.value)}
                    placeholder="valor"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="currency">Moeda</label>
                  <select
                    id="currency"
                    value={formData.currency || 'BRL'}
                    onChange={(e) => handleChange('currency', e.target.value)}
                  >
                    <option value="BRL">Real (R$)</option>
                    <option value="USD">Dólar ($)</option>
                    <option value="EUR">Euro (€)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="variable">Variável de Resultado</label>
                  <input
                    type="text"
                    id="variable"
                    value={formData.variable || ''}
                    onChange={(e) => handleChange('variable', e.target.value)}
                    placeholder="valor_formatado"
                  />
                </div>
              </>
            )}

            {/* String Transform */}
            {formData.actionType === 'string_transform' && (
              <>
                <div className="form-group">
                  <label htmlFor="sourceVariable">Variável de Origem</label>
                  <input
                    type="text"
                    id="sourceVariable"
                    value={formData.sourceVariable || ''}
                    onChange={(e) => handleChange('sourceVariable', e.target.value)}
                    placeholder="texto_original"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="transform">Transformação</label>
                  <select
                    id="transform"
                    value={formData.transform || 'uppercase'}
                    onChange={(e) => handleChange('transform', e.target.value)}
                  >
                    <option value="uppercase">MAIÚSCULAS</option>
                    <option value="lowercase">minúsculas</option>
                    <option value="capitalize">Primeira Letra Maiúscula</option>
                    <option value="trim">Remover Espaços</option>
                    <option value="reverse">Inverter</option>
                    <option value="slug">URL Slug (slug-format)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="variable">Variável de Resultado</label>
                  <input
                    type="text"
                    id="variable"
                    value={formData.variable || ''}
                    onChange={(e) => handleChange('variable', e.target.value)}
                    placeholder="texto_transformado"
                  />
                </div>
              </>
            )}

            {/* Array Operation */}
            {formData.actionType === 'array_operation' && (
              <>
                <div className="form-group">
                  <label htmlFor="arrayVariable">Variável do Array</label>
                  <input
                    type="text"
                    id="arrayVariable"
                    value={formData.arrayVariable || ''}
                    onChange={(e) => handleChange('arrayVariable', e.target.value)}
                    placeholder="meu_array"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="operation">Operação</label>
                  <select
                    id="operation"
                    value={formData.operation || 'push'}
                    onChange={(e) => handleChange('operation', e.target.value)}
                  >
                    <option value="push">Adicionar no Final (push)</option>
                    <option value="pop">Remover do Final (pop)</option>
                    <option value="shift">Remover do Início (shift)</option>
                    <option value="unshift">Adicionar no Início (unshift)</option>
                    <option value="length">Obter Tamanho (length)</option>
                    <option value="join">Juntar em String (join)</option>
                    <option value="reverse">Inverter (reverse)</option>
                    <option value="sort">Ordenar (sort)</option>
                    <option value="filter">Filtrar (filter)</option>
                    <option value="find">Encontrar (find)</option>
                  </select>
                </div>
                {(formData.operation === 'push' || formData.operation === 'unshift') && (
                  <div className="form-group">
                    <label htmlFor="value">Valor a Adicionar</label>
                    <input
                      type="text"
                      id="value"
                      value={formData.value || ''}
                      onChange={(e) => handleChange('value', e.target.value)}
                      placeholder="novo_valor ou {{variavel}}"
                    />
                  </div>
                )}
                {formData.operation === 'join' && (
                  <div className="form-group">
                    <label htmlFor="separator">Separador</label>
                    <input
                      type="text"
                      id="separator"
                      value={formData.separator || ', '}
                      onChange={(e) => handleChange('separator', e.target.value)}
                      placeholder=", "
                    />
                  </div>
                )}
                {(formData.operation === 'filter' || formData.operation === 'find') && (
                  <div className="form-group">
                    <label htmlFor="condition">Condição</label>
                    <input
                      type="text"
                      id="condition"
                      value={formData.condition || ''}
                      onChange={(e) => handleChange('condition', e.target.value)}
                      placeholder="item.status === 'ativo'"
                    />
                    <small>Use 'item' para referenciar cada elemento</small>
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="variable">Variável de Resultado</label>
                  <input
                    type="text"
                    id="variable"
                    value={formData.variable || ''}
                    onChange={(e) => handleChange('variable', e.target.value)}
                    placeholder="resultado"
                  />
                </div>
              </>
            )}

            {/* JSON Extract */}
            {formData.actionType === 'json_extract' && (
              <>
                <div className="form-group">
                  <label htmlFor="sourceVariable">Variável JSON de Origem</label>
                  <input
                    type="text"
                    id="sourceVariable"
                    value={formData.sourceVariable || ''}
                    onChange={(e) => handleChange('sourceVariable', e.target.value)}
                    placeholder="dados_json"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="path">Caminho (JSON Path)</label>
                  <input
                    type="text"
                    id="path"
                    value={formData.path || ''}
                    onChange={(e) => handleChange('path', e.target.value)}
                    placeholder="data.user.name ou items[0].id"
                  />
                  <small>Use ponto (.) para objetos e [n] para arrays</small>
                </div>
                <div className="form-group">
                  <label htmlFor="variable">Variável de Resultado</label>
                  <input
                    type="text"
                    id="variable"
                    value={formData.variable || ''}
                    onChange={(e) => handleChange('variable', e.target.value)}
                    placeholder="valor_extraido"
                  />
                </div>
              </>
            )}

            {/* Conditional Set */}
            {formData.actionType === 'conditional_set' && (
              <>
                <div className="form-group">
                  <label htmlFor="condition">Condição</label>
                  <input
                    type="text"
                    id="condition"
                    value={formData.condition || ''}
                    onChange={(e) => handleChange('condition', e.target.value)}
                    placeholder="{{status}} === 'ativo'"
                  />
                  <small>Expressão que retorna verdadeiro ou falso</small>
                </div>
                <div className="form-group">
                  <label htmlFor="variable">Nome da Variável</label>
                  <input
                    type="text"
                    id="variable"
                    value={formData.variable || ''}
                    onChange={(e) => handleChange('variable', e.target.value)}
                    placeholder="resultado"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="trueValue">Valor se Verdadeiro</label>
                  <input
                    type="text"
                    id="trueValue"
                    value={formData.trueValue || ''}
                    onChange={(e) => handleChange('trueValue', e.target.value)}
                    placeholder="sim"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="falseValue">Valor se Falso</label>
                  <input
                    type="text"
                    id="falseValue"
                    value={formData.falseValue || ''}
                    onChange={(e) => handleChange('falseValue', e.target.value)}
                    placeholder="não"
                  />
                </div>
              </>
            )}

            {/* Log */}
            {formData.actionType === 'log' && (
              <>
                <div className="form-group">
                  <label htmlFor="message">Mensagem de Log</label>
                  <textarea
                    id="message"
                    value={formData.message || ''}
                    onChange={(e) => handleChange('message', e.target.value)}
                    placeholder="Valor da variável: {{minha_variavel}}"
                    rows={3}
                  />
                  <small>Use {'{{variavel}}'} para incluir valores de variáveis</small>
                </div>
                <div className="form-group">
                  <label htmlFor="level">Nível</label>
                  <select
                    id="level"
                    value={formData.level || 'info'}
                    onChange={(e) => handleChange('level', e.target.value)}
                  >
                    <option value="debug">Debug</option>
                    <option value="info">Info</option>
                    <option value="warn">Warning</option>
                    <option value="error">Error</option>
                  </select>
                </div>
              </>
            )}

            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva o que esta ação faz..."
                rows={2}
              />
            </div>
          </>
        );

      case 'webhook':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do nó"
              />
            </div>
            <div className="form-group">
              <label htmlFor="method">Método HTTP</label>
              <select
                id="method"
                value={formData.method || 'GET'}
                onChange={(e) => handleChange('method', e.target.value)}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="url">URL</label>
              <input
                type="text"
                id="url"
                value={formData.url || ''}
                onChange={(e) => handleChange('url', e.target.value)}
                placeholder="https://api.exemplo.com/endpoint"
              />
            </div>
            <div className="form-group">
              <label htmlFor="body">Body (JSON)</label>
              <textarea
                id="body"
                value={formData.body || ''}
                onChange={(e) => handleChange('body', e.target.value)}
                placeholder='{"chave": "valor"}'
                rows={4}
              />
              <small>Use variáveis com {'{'}{'{'} nome_variavel {'}'}{'}'}</small>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva o que esta chamada faz..."
                rows={2}
              />
            </div>
          </>
        );

      case 'delay':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do nó"
              />
            </div>
            <div className="form-group">
              <label htmlFor="delayType">Tipo de Delay</label>
              <select
                id="delayType"
                value={formData.delayType || 'seconds'}
                onChange={(e) => handleChange('delayType', e.target.value)}
              >
                <option value="seconds">Segundos</option>
                <option value="minutes">Minutos</option>
                <option value="hours">Horas</option>
                <option value="days">Dias</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="delayValue">Valor</label>
              <input
                type="number"
                id="delayValue"
                value={formData.delayValue || 1}
                onChange={(e) => handleChange('delayValue', parseInt(e.target.value))}
                placeholder="1"
                min="1"
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva o delay..."
                rows={2}
              />
            </div>
          </>
        );

      case 'database':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do nó"
              />
            </div>
            <div className="form-group">
              <label htmlFor="operation">Operação</label>
              <select
                id="operation"
                value={formData.operation || 'select'}
                onChange={(e) => handleChange('operation', e.target.value)}
              >
                <option value="select">SELECT - Consultar</option>
                <option value="insert">INSERT - Inserir</option>
                <option value="update">UPDATE - Atualizar</option>
                <option value="delete">DELETE - Deletar</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="table">Tabela</label>
              <input
                type="text"
                id="table"
                value={formData.table || ''}
                onChange={(e) => handleChange('table', e.target.value)}
                placeholder="nome_da_tabela"
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva a operação..."
                rows={2}
              />
            </div>
          </>
        );

      case 'http_request':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do nó"
              />
            </div>
            <div className="form-group">
              <label htmlFor="method">Método HTTP</label>
              <select
                id="method"
                value={formData.method || 'GET'}
                onChange={(e) => handleChange('method', e.target.value)}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="url">URL</label>
              <input
                type="text"
                id="url"
                value={formData.url || ''}
                onChange={(e) => handleChange('url', e.target.value)}
                placeholder="https://api.exemplo.com/endpoint"
              />
            </div>
            <div className="form-group">
              <label htmlFor="body">Body (JSON)</label>
              <textarea
                id="body"
                value={formData.body || ''}
                onChange={(e) => handleChange('body', e.target.value)}
                placeholder='{"chave": "valor"}'
                rows={4}
              />
              <small>Use variáveis com {'{'}{'{'} nome_variavel {'}'}{'}'}</small>
            </div>
            <div className="form-group">
              <label htmlFor="responseVariable">Variável de Resposta</label>
              <input
                type="text"
                id="responseVariable"
                value={formData.responseVariable || ''}
                onChange={(e) => handleChange('responseVariable', e.target.value)}
                placeholder="response_data"
              />
              <small>Nome da variável para armazenar a resposta</small>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva a requisição..."
                rows={2}
              />
            </div>
          </>
        );

      case 'javascript_function':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome da função"
              />
            </div>
            <div className="form-group">
              <label htmlFor="code">Código JavaScript</label>
              <textarea
                id="code"
                value={formData.code || ''}
                onChange={(e) => handleChange('code', e.target.value)}
                placeholder="// Seu código aqui\nreturn { resultado: 'ok' };"
                rows={10}
                style={{ fontFamily: 'monospace', fontSize: '14px' }}
              />
              <small>Use 'return' para retornar valores</small>
            </div>
            <div className="form-group">
              <label htmlFor="outputVariable">Variável de Saída</label>
              <input
                type="text"
                id="outputVariable"
                value={formData.outputVariable || ''}
                onChange={(e) => handleChange('outputVariable', e.target.value)}
                placeholder="result"
              />
              <small>Nome da variável para armazenar o resultado</small>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva o que a função faz..."
                rows={2}
              />
            </div>
          </>
        );

      case 'webhook_trigger':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do trigger"
              />
            </div>
            <div className="form-group">
              <label htmlFor="webhookPath">Caminho do Webhook</label>
              <input
                type="text"
                id="webhookPath"
                value={formData.webhookPath || ''}
                onChange={(e) => handleChange('webhookPath', e.target.value)}
                placeholder="/webhook/meu-flow"
              />
              <small>URL que receberá as requisições externas</small>
            </div>
            <div className="form-group">
              <label htmlFor="method">Método HTTP</label>
              <select
                id="method"
                value={formData.method || 'POST'}
                onChange={(e) => handleChange('method', e.target.value)}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="authentication">Autenticação</label>
              <select
                id="authentication"
                value={formData.authentication || 'none'}
                onChange={(e) => handleChange('authentication', e.target.value)}
              >
                <option value="none">Nenhuma</option>
                <option value="bearer">Bearer Token</option>
                <option value="api_key">API Key</option>
                <option value="basic">Basic Auth</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva o webhook..."
                rows={2}
              />
            </div>
          </>
        );

      case 'internal_api':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome da operação"
              />
            </div>
            <div className="form-group">
              <label htmlFor="entity">Entidade</label>
              <select
                id="entity"
                value={formData.entity || ''}
                onChange={(e) => handleChange('entity', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="vehicles">Veículos</option>
                <option value="drivers">Motoristas</option>
                <option value="checklists">Checklists</option>
                <option value="reports">Relatórios</option>
                <option value="users">Usuários</option>
                <option value="companies">Empresas</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="operation">Operação</label>
              <select
                id="operation"
                value={formData.operation || 'read'}
                onChange={(e) => handleChange('operation', e.target.value)}
              >
                <option value="create">Criar</option>
                <option value="read">Ler</option>
                <option value="update">Atualizar</option>
                <option value="delete">Deletar</option>
                <option value="list">Listar</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="endpoint">Endpoint (opcional)</label>
              <input
                type="text"
                id="endpoint"
                value={formData.endpoint || ''}
                onChange={(e) => handleChange('endpoint', e.target.value)}
                placeholder="/api/custom-endpoint"
              />
              <small>Deixe vazio para usar endpoint padrão da entidade</small>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva a operação..."
                rows={2}
              />
            </div>
          </>
        );

      case 'whatsapp_advanced':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome da operação"
              />
            </div>
            <div className="form-group">
              <label htmlFor="category">Categoria</label>
              <select
                id="category"
                value={formData.category || 'messages'}
                onChange={(e) => handleChange('category', e.target.value)}
              >
                <option value="messages">Mensagens</option>
                <option value="chats">Chats</option>
                <option value="groups">Grupos</option>
                <option value="profile">Perfil</option>
                <option value="settings">Configurações</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="operation">Operação</label>
              <select
                id="operation"
                value={formData.operation || 'send_text'}
                onChange={(e) => handleChange('operation', e.target.value)}
              >
                <optgroup label="Mensagens">
                  <option value="send_text">Enviar Texto</option>
                  <option value="send_media">Enviar Mídia</option>
                  <option value="send_audio">Enviar Áudio</option>
                  <option value="send_location">Enviar Localização</option>
                  <option value="send_contact">Enviar Contato</option>
                  <option value="send_poll">Enviar Enquete</option>
                  <option value="send_list">Enviar Lista</option>
                  <option value="send_buttons">Enviar Botões</option>
                </optgroup>
                <optgroup label="Chats">
                  <option value="mark_read">Marcar como Lido</option>
                  <option value="mark_unread">Marcar como Não Lido</option>
                  <option value="archive_chat">Arquivar Chat</option>
                </optgroup>
                <optgroup label="Grupos">
                  <option value="create_group">Criar Grupo</option>
                  <option value="add_participants">Adicionar Membros</option>
                  <option value="remove_participants">Remover Membros</option>
                </optgroup>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="phoneNumber">Número de Telefone</label>
              <input
                type="text"
                id="phoneNumber"
                value={formData.phoneNumber || ''}
                onChange={(e) => handleChange('phoneNumber', e.target.value)}
                placeholder="5511999999999"
              />
              <small>Formato: código do país + DDD + número</small>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva a operação..."
                rows={2}
              />
            </div>
          </>
        );

      case 'loop':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do loop"
              />
            </div>
            <div className="form-group">
              <label htmlFor="loopType">Tipo de Loop</label>
              <select
                id="loopType"
                value={formData.loopType || 'forEach'}
                onChange={(e) => handleChange('loopType', e.target.value)}
              >
                <option value="forEach">For Each (iterar array)</option>
                <option value="while">While (condicional)</option>
                <option value="repeat">Repeat (N vezes)</option>
              </select>
            </div>
            {formData.loopType === 'forEach' && (
              <>
                <div className="form-group">
                  <label htmlFor="arrayVariable">Variável do Array</label>
                  <input
                    type="text"
                    id="arrayVariable"
                    value={formData.arrayVariable || ''}
                    onChange={(e) => handleChange('arrayVariable', e.target.value)}
                    placeholder="items"
                  />
                  <small>Nome da variável que contém o array</small>
                </div>
                <div className="form-group">
                  <label htmlFor="itemVariable">Variável do Item</label>
                  <input
                    type="text"
                    id="itemVariable"
                    value={formData.itemVariable || ''}
                    onChange={(e) => handleChange('itemVariable', e.target.value)}
                    placeholder="item"
                  />
                  <small>Nome da variável para cada item do loop</small>
                </div>
              </>
            )}
            {formData.loopType === 'while' && (
              <div className="form-group">
                <label htmlFor="condition">Condição</label>
                <input
                  type="text"
                  id="condition"
                  value={formData.condition || ''}
                  onChange={(e) => handleChange('condition', e.target.value)}
                  placeholder="counter < 10"
                />
                <small>Loop continua enquanto condição for verdadeira</small>
              </div>
            )}
            {formData.loopType === 'repeat' && (
              <div className="form-group">
                <label htmlFor="repeatCount">Número de Repetições</label>
                <input
                  type="number"
                  id="repeatCount"
                  value={formData.repeatCount || 10}
                  onChange={(e) => handleChange('repeatCount', parseInt(e.target.value))}
                  placeholder="10"
                  min="1"
                />
              </div>
            )}
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva o propósito do loop..."
                rows={2}
              />
            </div>
          </>
        );

      case 'error_handler':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do tratamento de erro"
              />
            </div>
            <div className="form-group">
              <label htmlFor="retryCount">Tentativas de Retry</label>
              <input
                type="number"
                id="retryCount"
                value={formData.retryCount || 3}
                onChange={(e) => handleChange('retryCount', parseInt(e.target.value))}
                placeholder="3"
                min="0"
                max="10"
              />
              <small>Número de tentativas antes de falhar (0 = sem retry)</small>
            </div>
            <div className="form-group">
              <label htmlFor="retryDelay">Delay entre Retries (ms)</label>
              <input
                type="number"
                id="retryDelay"
                value={formData.retryDelay || 1000}
                onChange={(e) => handleChange('retryDelay', parseInt(e.target.value))}
                placeholder="1000"
                min="100"
                step="100"
              />
              <small>Tempo de espera entre tentativas (milissegundos)</small>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.continueOnError || false}
                  onChange={(e) => handleChange('continueOnError', e.target.checked)}
                />
                {' '}Continuar fluxo mesmo com erro
              </label>
              <small>Se marcado, segue para próximo node mesmo se falhar</small>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva o tratamento de erro..."
                rows={2}
              />
            </div>
          </>
        );

      case 'email':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do email"
              />
            </div>
            <div className="form-group">
              <label htmlFor="to">Para (To)</label>
              <input
                type="text"
                id="to"
                value={formData.to || ''}
                onChange={(e) => handleChange('to', e.target.value)}
                placeholder="destinatario@email.com"
              />
              <small>Use variáveis: {'{'}{'{'} email {'}'}{'}'}</small>
            </div>
            <div className="form-group">
              <label htmlFor="subject">Assunto</label>
              <input
                type="text"
                id="subject"
                value={formData.subject || ''}
                onChange={(e) => handleChange('subject', e.target.value)}
                placeholder="Assunto do email"
              />
            </div>
            <div className="form-group">
              <label htmlFor="body">Corpo do Email</label>
              <textarea
                id="body"
                value={formData.body || ''}
                onChange={(e) => handleChange('body', e.target.value)}
                placeholder="Corpo do email em HTML ou texto..."
                rows={6}
              />
              <small>Suporta HTML e variáveis {'{'}{'{'} nome {'}'}{'}'}</small>
            </div>
            <div className="form-group">
              <label htmlFor="template">Template (opcional)</label>
              <select
                id="template"
                value={formData.template || ''}
                onChange={(e) => handleChange('template', e.target.value)}
              >
                <option value="">Sem template</option>
                <option value="welcome">Boas-vindas</option>
                <option value="notification">Notificação</option>
                <option value="report">Relatório</option>
                <option value="invoice">Fatura</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="cc">CC (opcional)</label>
              <input
                type="text"
                id="cc"
                value={formData.cc || ''}
                onChange={(e) => handleChange('cc', e.target.value)}
                placeholder="cc@email.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="bcc">BCC (opcional)</label>
              <input
                type="text"
                id="bcc"
                value={formData.bcc || ''}
                onChange={(e) => handleChange('bcc', e.target.value)}
                placeholder="bcc@email.com"
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.hasAttachments || false}
                  onChange={(e) => handleChange('hasAttachments', e.target.checked)}
                />
                {' '}Incluir anexos
              </label>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva o email..."
                rows={2}
              />
            </div>
          </>
        );

      case 'scheduler':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do agendamento"
              />
            </div>
            <div className="form-group">
              <label htmlFor="scheduleType">Tipo de Agendamento</label>
              <select
                id="scheduleType"
                value={formData.scheduleType || 'daily'}
                onChange={(e) => handleChange('scheduleType', e.target.value)}
              >
                <option value="daily">Diariamente</option>
                <option value="weekly">Semanalmente</option>
                <option value="monthly">Mensalmente</option>
                <option value="interval">Intervalo</option>
                <option value="cron">Cron (avançado)</option>
              </select>
            </div>
            {formData.scheduleType === 'daily' && (
              <div className="form-group">
                <label htmlFor="time">Horário</label>
                <input
                  type="time"
                  id="time"
                  value={formData.time || '09:00'}
                  onChange={(e) => handleChange('time', e.target.value)}
                />
              </div>
            )}
            {formData.scheduleType === 'weekly' && (
              <>
                <div className="form-group">
                  <label htmlFor="frequency">Dia da Semana</label>
                  <select
                    id="frequency"
                    value={formData.frequency || 'Monday'}
                    onChange={(e) => handleChange('frequency', e.target.value)}
                  >
                    <option value="Monday">Segunda-feira</option>
                    <option value="Tuesday">Terça-feira</option>
                    <option value="Wednesday">Quarta-feira</option>
                    <option value="Thursday">Quinta-feira</option>
                    <option value="Friday">Sexta-feira</option>
                    <option value="Saturday">Sábado</option>
                    <option value="Sunday">Domingo</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="time">Horário</label>
                  <input
                    type="time"
                    id="time"
                    value={formData.time || '09:00'}
                    onChange={(e) => handleChange('time', e.target.value)}
                  />
                </div>
              </>
            )}
            {formData.scheduleType === 'monthly' && (
              <>
                <div className="form-group">
                  <label htmlFor="frequency">Dia do Mês</label>
                  <input
                    type="number"
                    id="frequency"
                    value={formData.frequency || 1}
                    onChange={(e) => handleChange('frequency', e.target.value)}
                    min="1"
                    max="31"
                    placeholder="1"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="time">Horário</label>
                  <input
                    type="time"
                    id="time"
                    value={formData.time || '09:00'}
                    onChange={(e) => handleChange('time', e.target.value)}
                  />
                </div>
              </>
            )}
            {formData.scheduleType === 'interval' && (
              <div className="form-group">
                <label htmlFor="frequency">Intervalo (minutos)</label>
                <input
                  type="number"
                  id="frequency"
                  value={formData.frequency || 30}
                  onChange={(e) => handleChange('frequency', e.target.value)}
                  min="1"
                  placeholder="30"
                />
                <small>Executar a cada X minutos</small>
              </div>
            )}
            {formData.scheduleType === 'cron' && (
              <div className="form-group">
                <label htmlFor="cronExpression">Expressão Cron</label>
                <input
                  type="text"
                  id="cronExpression"
                  value={formData.cronExpression || '0 9 * * *'}
                  onChange={(e) => handleChange('cronExpression', e.target.value)}
                  placeholder="0 9 * * *"
                />
                <small>Formato: minuto hora dia mês dia-da-semana</small>
              </div>
            )}
            <div className="form-group">
              <label htmlFor="timezone">Timezone</label>
              <select
                id="timezone"
                value={formData.timezone || 'America/Sao_Paulo'}
                onChange={(e) => handleChange('timezone', e.target.value)}
              >
                <option value="America/Sao_Paulo">São Paulo (GMT-3)</option>
                <option value="America/New_York">New York (GMT-5)</option>
                <option value="Europe/London">Londres (GMT+0)</option>
                <option value="Europe/Paris">Paris (GMT+1)</option>
                <option value="Asia/Tokyo">Tóquio (GMT+9)</option>
              </select>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.enabled !== false}
                  onChange={(e) => handleChange('enabled', e.target.checked)}
                />
                {' '}Agendamento ativo
              </label>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva o agendamento..."
                rows={2}
              />
            </div>
          </>
        );

      case 'menu':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do menu"
              />
            </div>
            <div className="form-group">
              <label htmlFor="title">Título do Menu</label>
              <input
                type="text"
                id="title"
                value={formData.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="ex: Menu Principal"
              />
            </div>
            <div className="form-group">
              <label htmlFor="variable">Variável para Resposta</label>
              <input
                type="text"
                id="variable"
                value={formData.variable || 'menu_choice'}
                onChange={(e) => handleChange('variable', e.target.value)}
                placeholder="menu_choice"
              />
            </div>
            <div className="form-group">
              <label>Opções do Menu</label>
              <div className="options-list">
                {(formData.options || []).map((option: { label: string; value?: string; description?: string }, index: number) => (
                  <div key={index} className="option-item" style={{ marginBottom: '8px', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 'bold', color: '#6b7280' }}>{index + 1}.</span>
                      <input
                        type="text"
                        value={option.label || ''}
                        onChange={(e) => {
                          const newOptions = [...(formData.options || [])];
                          newOptions[index] = { ...newOptions[index], label: e.target.value };
                          handleChange('options', newOptions);
                        }}
                        placeholder="Texto da opção"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newOptions = (formData.options || []).filter((_: unknown, i: number) => i !== index);
                          handleChange('options', newOptions);
                        }}
                        style={{ padding: '4px 8px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        X
                      </button>
                    </div>
                    <input
                      type="text"
                      value={option.description || ''}
                      onChange={(e) => {
                        const newOptions = [...(formData.options || [])];
                        newOptions[index] = { ...newOptions[index], description: e.target.value };
                        handleChange('options', newOptions);
                      }}
                      placeholder="Descrição (opcional)"
                      style={{ width: '100%', fontSize: '12px' }}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const newOptions = [...(formData.options || []), { label: '', value: '', description: '' }];
                    handleChange('options', newOptions);
                  }}
                  style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', width: '100%' }}
                >
                  + Adicionar Opção
                </button>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="invalidMessage">Mensagem de Opção Inválida</label>
              <input
                type="text"
                id="invalidMessage"
                value={formData.invalidMessage || ''}
                onChange={(e) => handleChange('invalidMessage', e.target.value)}
                placeholder="Opção inválida. Escolha uma opção válida."
              />
            </div>
          </>
        );

      case 'checklist':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do nó"
              />
            </div>
            <div className="form-group">
              <label htmlFor="operation">Operação</label>
              <select
                id="operation"
                value={formData.operation || 'create'}
                onChange={(e) => handleChange('operation', e.target.value)}
              >
                <option value="create">Criar Checklist</option>
                <option value="find">Buscar Checklist</option>
                <option value="update">Atualizar Checklist</option>
                <option value="complete">Concluir Checklist</option>
                <option value="list">Listar Checklists</option>
              </select>
            </div>
            {formData.operation === 'create' && (
              <>
                <div className="form-group">
                  <label htmlFor="tipo">Tipo de Checklist</label>
                  <select
                    id="tipo"
                    value={formData.tipo || 'diario'}
                    onChange={(e) => handleChange('tipo', e.target.value)}
                  >
                    <option value="diario">Diário</option>
                    <option value="pre_viagem">Pré-Viagem</option>
                    <option value="pos_viagem">Pós-Viagem</option>
                    <option value="semanal">Semanal</option>
                    <option value="mensal">Mensal</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="veiculo_id">ID do Veículo (variável)</label>
                  <input
                    type="text"
                    id="veiculo_id"
                    value={formData.veiculo_id || ''}
                    onChange={(e) => handleChange('veiculo_id', e.target.value)}
                    placeholder="{{veiculo_id}} ou ID fixo"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="motorista_id">ID do Motorista (variável)</label>
                  <input
                    type="text"
                    id="motorista_id"
                    value={formData.motorista_id || ''}
                    onChange={(e) => handleChange('motorista_id', e.target.value)}
                    placeholder="{{motorista_id}} ou ID fixo"
                  />
                </div>
              </>
            )}
            {(formData.operation === 'find' || formData.operation === 'update' || formData.operation === 'complete') && (
              <div className="form-group">
                <label htmlFor="checklistId">ID do Checklist</label>
                <input
                  type="text"
                  id="checklistId"
                  value={formData.checklistId || ''}
                  onChange={(e) => handleChange('checklistId', e.target.value)}
                  placeholder="{{checklist_id}} ou ID fixo"
                />
              </div>
            )}
            {formData.operation === 'update' && (
              <>
                <div className="form-group">
                  <label htmlFor="status">Novo Status</label>
                  <select
                    id="status"
                    value={formData.status || ''}
                    onChange={(e) => handleChange('status', e.target.value)}
                  >
                    <option value="">Manter atual</option>
                    <option value="pendente">Pendente</option>
                    <option value="em_andamento">Em Andamento</option>
                    <option value="concluido">Concluído</option>
                    <option value="reprovado">Reprovado</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="observacoes">Observações</label>
                  <textarea
                    id="observacoes"
                    value={formData.observacoes || ''}
                    onChange={(e) => handleChange('observacoes', e.target.value)}
                    placeholder="Observações do checklist..."
                    rows={3}
                  />
                </div>
              </>
            )}
            <div className="form-group">
              <label htmlFor="description">Descrição do Nó</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva a operação..."
                rows={2}
              />
            </div>
          </>
        );

      case 'vehicle':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do nó"
              />
            </div>
            <div className="form-group">
              <label htmlFor="operation">Operação</label>
              <select
                id="operation"
                value={formData.operation || 'find'}
                onChange={(e) => handleChange('operation', e.target.value)}
              >
                <option value="find">Buscar Veículo</option>
                <option value="create">Criar Veículo</option>
                <option value="update">Atualizar Veículo</option>
                <option value="update_odometer">Atualizar Odômetro</option>
                <option value="list">Listar Veículos</option>
              </select>
            </div>
            {formData.operation === 'find' && (
              <>
                <div className="form-group">
                  <label htmlFor="vehicleId">ID do Veículo</label>
                  <input
                    type="text"
                    id="vehicleId"
                    value={formData.vehicleId || ''}
                    onChange={(e) => handleChange('vehicleId', e.target.value)}
                    placeholder="{{veiculo_id}} ou ID fixo"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="placa">Ou Placa</label>
                  <input
                    type="text"
                    id="placa"
                    value={formData.placa || ''}
                    onChange={(e) => handleChange('placa', e.target.value)}
                    placeholder="ABC-1234 ou {{placa}}"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="tag">Ou TAG</label>
                  <input
                    type="text"
                    id="tag"
                    value={formData.tag || ''}
                    onChange={(e) => handleChange('tag', e.target.value)}
                    placeholder="TAG123 ou {{tag}}"
                  />
                </div>
              </>
            )}
            {formData.operation === 'create' && (
              <>
                <div className="form-group">
                  <label htmlFor="placa">Placa *</label>
                  <input
                    type="text"
                    id="placa"
                    value={formData.placa || ''}
                    onChange={(e) => handleChange('placa', e.target.value)}
                    placeholder="ABC-1234"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="marca">Marca</label>
                  <input
                    type="text"
                    id="marca"
                    value={formData.marca || ''}
                    onChange={(e) => handleChange('marca', e.target.value)}
                    placeholder="Volkswagen"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="modelo">Modelo</label>
                  <input
                    type="text"
                    id="modelo"
                    value={formData.modelo || ''}
                    onChange={(e) => handleChange('modelo', e.target.value)}
                    placeholder="Gol"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="tipo">Tipo</label>
                  <select
                    id="tipo"
                    value={formData.tipo || ''}
                    onChange={(e) => handleChange('tipo', e.target.value)}
                  >
                    <option value="">Selecione</option>
                    <option value="carro">Carro</option>
                    <option value="moto">Moto</option>
                    <option value="caminhao">Caminhão</option>
                    <option value="onibus">Ônibus</option>
                    <option value="van">Van</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="combustivel">Combustível</label>
                  <select
                    id="combustivel"
                    value={formData.combustivel || ''}
                    onChange={(e) => handleChange('combustivel', e.target.value)}
                  >
                    <option value="">Selecione</option>
                    <option value="gasolina">Gasolina</option>
                    <option value="etanol">Etanol</option>
                    <option value="diesel">Diesel</option>
                    <option value="flex">Flex</option>
                    <option value="eletrico">Elétrico</option>
                  </select>
                </div>
              </>
            )}
            {formData.operation === 'update_odometer' && (
              <>
                <div className="form-group">
                  <label htmlFor="vehicleId">ID do Veículo</label>
                  <input
                    type="text"
                    id="vehicleId"
                    value={formData.vehicleId || ''}
                    onChange={(e) => handleChange('vehicleId', e.target.value)}
                    placeholder="{{veiculo_id}}"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="quilometragem">Nova Quilometragem</label>
                  <input
                    type="text"
                    id="quilometragem"
                    value={formData.quilometragem || ''}
                    onChange={(e) => handleChange('quilometragem', e.target.value)}
                    placeholder="{{odometro}} ou valor fixo"
                  />
                </div>
              </>
            )}
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva a operação..."
                rows={2}
              />
            </div>
          </>
        );

      case 'driver':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do nó"
              />
            </div>
            <div className="form-group">
              <label htmlFor="operation">Operação</label>
              <select
                id="operation"
                value={formData.operation || 'find'}
                onChange={(e) => handleChange('operation', e.target.value)}
              >
                <option value="find">Buscar Motorista</option>
                <option value="create">Criar Motorista</option>
                <option value="update">Atualizar Motorista</option>
                <option value="list">Listar Motoristas</option>
              </select>
            </div>
            {formData.operation === 'find' && (
              <>
                <div className="form-group">
                  <label htmlFor="driverId">ID do Motorista</label>
                  <input
                    type="text"
                    id="driverId"
                    value={formData.driverId || ''}
                    onChange={(e) => handleChange('driverId', e.target.value)}
                    placeholder="{{motorista_id}} ou ID fixo"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cpf">Ou CPF</label>
                  <input
                    type="text"
                    id="cpf"
                    value={formData.cpf || ''}
                    onChange={(e) => handleChange('cpf', e.target.value)}
                    placeholder="123.456.789-00 ou {{cpf}}"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="celular">Ou Celular</label>
                  <input
                    type="text"
                    id="celular"
                    value={formData.celular || ''}
                    onChange={(e) => handleChange('celular', e.target.value)}
                    placeholder="11999999999 ou {{celular}}"
                  />
                  <small>Deixe vazio para usar o telefone do WhatsApp</small>
                </div>
              </>
            )}
            {formData.operation === 'create' && (
              <>
                <div className="form-group">
                  <label htmlFor="nome">Nome *</label>
                  <input
                    type="text"
                    id="nome"
                    value={formData.nome || ''}
                    onChange={(e) => handleChange('nome', e.target.value)}
                    placeholder="João da Silva"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cpf">CPF *</label>
                  <input
                    type="text"
                    id="cpf"
                    value={formData.cpf || ''}
                    onChange={(e) => handleChange('cpf', e.target.value)}
                    placeholder="123.456.789-00"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="celular">Celular</label>
                  <input
                    type="text"
                    id="celular"
                    value={formData.celular || ''}
                    onChange={(e) => handleChange('celular', e.target.value)}
                    placeholder="11999999999"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email || ''}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="joao@email.com"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cnh_numero">CNH</label>
                  <input
                    type="text"
                    id="cnh_numero"
                    value={formData.cnh_numero || ''}
                    onChange={(e) => handleChange('cnh_numero', e.target.value)}
                    placeholder="12345678901"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cnh_categoria">Categoria CNH</label>
                  <select
                    id="cnh_categoria"
                    value={formData.cnh_categoria || ''}
                    onChange={(e) => handleChange('cnh_categoria', e.target.value)}
                  >
                    <option value="">Selecione</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="AB">AB</option>
                    <option value="C">C</option>
                    <option value="D">D</option>
                    <option value="E">E</option>
                  </select>
                </div>
              </>
            )}
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva a operação..."
                rows={2}
              />
            </div>
          </>
        );

      case 'abastecimento':
      case 'fueling':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do nó"
              />
            </div>
            <div className="form-group">
              <label htmlFor="operation">Operação</label>
              <select
                id="operation"
                value={formData.operation || 'create'}
                onChange={(e) => handleChange('operation', e.target.value)}
              >
                <option value="create">Registrar Abastecimento</option>
                <option value="find">Buscar Abastecimento</option>
                <option value="list">Listar Abastecimentos</option>
              </select>
            </div>
            {formData.operation === 'create' && (
              <>
                <div className="form-group">
                  <label htmlFor="veiculo_id">ID do Veículo</label>
                  <input
                    type="text"
                    id="veiculo_id"
                    value={formData.veiculo_id || ''}
                    onChange={(e) => handleChange('veiculo_id', e.target.value)}
                    placeholder="{{veiculo_id}}"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="motorista_id">ID do Motorista</label>
                  <input
                    type="text"
                    id="motorista_id"
                    value={formData.motorista_id || ''}
                    onChange={(e) => handleChange('motorista_id', e.target.value)}
                    placeholder="{{motorista_id}}"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="volume">Volume (litros)</label>
                  <input
                    type="text"
                    id="volume"
                    value={formData.volume || ''}
                    onChange={(e) => handleChange('volume', e.target.value)}
                    placeholder="{{volume}} ou valor fixo"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="custo">Custo (R$)</label>
                  <input
                    type="text"
                    id="custo"
                    value={formData.custo || ''}
                    onChange={(e) => handleChange('custo', e.target.value)}
                    placeholder="{{custo}} ou valor fixo"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="tipo_combustivel">Tipo de Combustível</label>
                  <select
                    id="tipo_combustivel"
                    value={formData.tipo_combustivel || ''}
                    onChange={(e) => handleChange('tipo_combustivel', e.target.value)}
                  >
                    <option value="">Selecione</option>
                    <option value="gasolina">Gasolina</option>
                    <option value="etanol">Etanol</option>
                    <option value="diesel">Diesel</option>
                    <option value="diesel_s10">Diesel S10</option>
                    <option value="gnv">GNV</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="odometro">Odômetro</label>
                  <input
                    type="text"
                    id="odometro"
                    value={formData.odometro || ''}
                    onChange={(e) => handleChange('odometro', e.target.value)}
                    placeholder="{{odometro}}"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="posto_nome">Nome do Posto</label>
                  <input
                    type="text"
                    id="posto_nome"
                    value={formData.posto_nome || ''}
                    onChange={(e) => handleChange('posto_nome', e.target.value)}
                    placeholder="{{posto_nome}}"
                  />
                </div>
              </>
            )}
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva a operação..."
                rows={2}
              />
            </div>
          </>
        );

      case 'maintenance':
      case 'manutencao':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do nó"
              />
            </div>
            <div className="form-group">
              <label htmlFor="maintenanceType">Tipo de Manutenção</label>
              <select
                id="maintenanceType"
                value={formData.maintenanceType || 'preventive'}
                onChange={(e) => handleChange('maintenanceType', e.target.value)}
              >
                <option value="preventive">Preventiva</option>
                <option value="corrective">Corretiva</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="operation">Operação</label>
              <select
                id="operation"
                value={formData.operation || 'create'}
                onChange={(e) => handleChange('operation', e.target.value)}
              >
                <option value="create">Criar Manutenção</option>
                <option value="find">Buscar Manutenção</option>
                <option value="update">Atualizar Manutenção</option>
                <option value="list">Listar Manutenções</option>
              </select>
            </div>
            {formData.operation === 'create' && (
              <>
                <div className="form-group">
                  <label htmlFor="veiculo_id">ID do Veículo</label>
                  <input
                    type="text"
                    id="veiculo_id"
                    value={formData.veiculo_id || ''}
                    onChange={(e) => handleChange('veiculo_id', e.target.value)}
                    placeholder="{{veiculo_id}}"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="descricao">Descrição *</label>
                  <textarea
                    id="descricao"
                    value={formData.descricao || ''}
                    onChange={(e) => handleChange('descricao', e.target.value)}
                    placeholder="Descrição da manutenção..."
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="tipo_manutencao">Tipo Específico</label>
                  <select
                    id="tipo_manutencao"
                    value={formData.tipo_manutencao || ''}
                    onChange={(e) => handleChange('tipo_manutencao', e.target.value)}
                  >
                    <option value="">Selecione</option>
                    <option value="troca_oleo">Troca de Óleo</option>
                    <option value="troca_filtros">Troca de Filtros</option>
                    <option value="troca_pneus">Troca de Pneus</option>
                    <option value="alinhamento">Alinhamento</option>
                    <option value="balanceamento">Balanceamento</option>
                    <option value="revisao">Revisão Completa</option>
                    <option value="freios">Manutenção de Freios</option>
                    <option value="suspensao">Manutenção de Suspensão</option>
                    <option value="eletrica">Manutenção Elétrica</option>
                    <option value="outros">Outros</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="prioridade">Prioridade</label>
                  <select
                    id="prioridade"
                    value={formData.prioridade || 'Média'}
                    onChange={(e) => handleChange('prioridade', e.target.value)}
                  >
                    <option value="Baixa">Baixa</option>
                    <option value="Média">Média</option>
                    <option value="Alta">Alta</option>
                    <option value="Urgente">Urgente</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="custo_estimado">Custo Estimado (R$)</label>
                  <input
                    type="text"
                    id="custo_estimado"
                    value={formData.custo_estimado || ''}
                    onChange={(e) => handleChange('custo_estimado', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </>
            )}
            {formData.operation === 'update' && (
              <>
                <div className="form-group">
                  <label htmlFor="maintenanceId">ID da Manutenção</label>
                  <input
                    type="text"
                    id="maintenanceId"
                    value={formData.maintenanceId || ''}
                    onChange={(e) => handleChange('maintenanceId', e.target.value)}
                    placeholder="{{manutencao_id}}"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="status">Novo Status</label>
                  <select
                    id="status"
                    value={formData.status || ''}
                    onChange={(e) => handleChange('status', e.target.value)}
                  >
                    <option value="">Manter atual</option>
                    <option value="Agendada">Agendada</option>
                    <option value="Em Andamento">Em Andamento</option>
                    <option value="Concluída">Concluída</option>
                    <option value="Cancelada">Cancelada</option>
                  </select>
                </div>
              </>
            )}
            <div className="form-group">
              <label htmlFor="description">Descrição do Nó</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva a operação..."
                rows={2}
              />
            </div>
          </>
        );

      case 'notification':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome da notificação"
              />
            </div>
            <div className="form-group">
              <label htmlFor="type">Tipo de Notificação</label>
              <select
                id="type"
                value={formData.type || 'info'}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                <option value="info">Informação</option>
                <option value="success">Sucesso</option>
                <option value="warning">Aviso</option>
                <option value="error">Erro</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="recipient">Destinatário</label>
              <input
                type="text"
                id="recipient"
                value={formData.recipient || ''}
                onChange={(e) => handleChange('recipient', e.target.value)}
                placeholder="{{usuario_id}} ou ID do usuário"
              />
              <small>ID do usuário ou variável. Deixe vazio para usar o usuário do contexto.</small>
            </div>
            <div className="form-group">
              <label htmlFor="title">Título</label>
              <input
                type="text"
                id="title"
                value={formData.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Título da notificação"
              />
              <small>Use {'{{variavel}}'} para incluir valores dinâmicos</small>
            </div>
            <div className="form-group">
              <label htmlFor="message">Mensagem *</label>
              <textarea
                id="message"
                value={formData.message || ''}
                onChange={(e) => handleChange('message', e.target.value)}
                placeholder="Conteúdo da notificação..."
                rows={4}
              />
              <small>Use {'{{variavel}}'} para incluir valores dinâmicos</small>
            </div>
            <div className="form-group">
              <label htmlFor="link">Link (opcional)</label>
              <input
                type="text"
                id="link"
                value={formData.link || ''}
                onChange={(e) => handleChange('link', e.target.value)}
                placeholder="/dashboard ou https://exemplo.com"
              />
              <small>URL para redirecionar quando a notificação for clicada</small>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva a notificação..."
                rows={2}
              />
            </div>
          </>
        );

      case 'report':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do relatório"
              />
            </div>
            <div className="form-group">
              <label htmlFor="reportType">Tipo de Relatório</label>
              <select
                id="reportType"
                value={formData.reportType || 'checklists'}
                onChange={(e) => handleChange('reportType', e.target.value)}
              >
                <option value="checklists">Checklists</option>
                <option value="abastecimentos">Abastecimentos</option>
                <option value="manutencoes">Manutenções</option>
                <option value="veiculos">Veículos</option>
                <option value="motoristas">Motoristas</option>
                <option value="ocorrencias">Ocorrências</option>
                <option value="custom">Consulta SQL Personalizada</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="format">Formato de Saída</label>
              <select
                id="format"
                value={formData.format || 'json'}
                onChange={(e) => handleChange('format', e.target.value)}
              >
                <option value="json">JSON</option>
                <option value="csv">CSV</option>
                <option value="excel">Excel</option>
                <option value="pdf">PDF</option>
                <option value="text">Texto Formatado</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="dateRange">Período</label>
              <select
                id="dateRange"
                value={formData.dateRange || 'today'}
                onChange={(e) => handleChange('dateRange', e.target.value)}
              >
                <option value="today">Hoje</option>
                <option value="yesterday">Ontem</option>
                <option value="last_7_days">Últimos 7 dias</option>
                <option value="last_30_days">Últimos 30 dias</option>
                <option value="this_month">Este mês</option>
                <option value="last_month">Mês passado</option>
                <option value="this_year">Este ano</option>
                <option value="custom">Personalizado</option>
              </select>
            </div>
            {formData.dateRange === 'custom' && (
              <>
                <div className="form-group">
                  <label htmlFor="startDate">Data Inicial</label>
                  <input
                    type="date"
                    id="startDate"
                    value={formData.startDate || ''}
                    onChange={(e) => handleChange('startDate', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="endDate">Data Final</label>
                  <input
                    type="date"
                    id="endDate"
                    value={formData.endDate || ''}
                    onChange={(e) => handleChange('endDate', e.target.value)}
                  />
                </div>
              </>
            )}
            {formData.reportType === 'custom' && (
              <div className="form-group">
                <label htmlFor="customQuery">Consulta SQL</label>
                <textarea
                  id="customQuery"
                  value={formData.customQuery || ''}
                  onChange={(e) => handleChange('customQuery', e.target.value)}
                  placeholder="SELECT * FROM tabela WHERE empresa_id = {{empresa_id}}"
                  rows={5}
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
                <small>Use {'{{variavel}}'} para incluir variáveis. A variável empresa_id é obrigatória.</small>
              </div>
            )}
            {formData.reportType !== 'custom' && (
              <div className="form-group">
                <label htmlFor="filters">Filtros (JSON)</label>
                <textarea
                  id="filters"
                  value={formData.filters || ''}
                  onChange={(e) => handleChange('filters', e.target.value)}
                  placeholder='{"status": "ativo", "tipo": "diario"}'
                  rows={3}
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
                <small>Filtros adicionais no formato JSON</small>
              </div>
            )}
            <div className="form-group">
              <label htmlFor="variable">Variável de Resultado</label>
              <input
                type="text"
                id="variable"
                value={formData.variable || 'report_data'}
                onChange={(e) => handleChange('variable', e.target.value)}
                placeholder="report_data"
              />
              <small>Nome da variável para armazenar o resultado</small>
            </div>
            <div className="form-group">
              <label htmlFor="limit">Limite de Registros</label>
              <input
                type="number"
                id="limit"
                value={formData.limit || 100}
                onChange={(e) => handleChange('limit', parseInt(e.target.value) || 100)}
                placeholder="100"
                min="1"
                max="10000"
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.includeStats || false}
                  onChange={(e) => handleChange('includeStats', e.target.checked)}
                />
                {' '}Incluir estatísticas (total, média, etc.)
              </label>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva o relatório..."
                rows={2}
              />
            </div>
          </>
        );

      case 'schedule':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do agendamento"
              />
            </div>
            <div className="form-group">
              <label htmlFor="operation">Operação</label>
              <select
                id="operation"
                value={formData.operation || 'create'}
                onChange={(e) => handleChange('operation', e.target.value)}
              >
                <option value="create">Criar Agendamento</option>
                <option value="update">Atualizar Agendamento</option>
                <option value="cancel">Cancelar Agendamento</option>
                <option value="list">Listar Agendamentos</option>
                <option value="check_due">Verificar Pendentes</option>
                <option value="complete">Marcar como Concluído</option>
              </select>
            </div>

            {/* Criar Agendamento */}
            {formData.operation === 'create' && (
              <>
                <div className="form-group">
                  <label htmlFor="scheduleType">Tipo de Agendamento</label>
                  <select
                    id="scheduleType"
                    value={formData.scheduleType || 'once'}
                    onChange={(e) => handleChange('scheduleType', e.target.value)}
                  >
                    <option value="once">Único</option>
                    <option value="daily">Diário</option>
                    <option value="weekly">Semanal</option>
                    <option value="monthly">Mensal</option>
                    <option value="custom">Personalizado (Cron)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="title">Título *</label>
                  <input
                    type="text"
                    id="title"
                    value={formData.title || ''}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Título do agendamento"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="scheduleDescription">Descrição do Evento</label>
                  <textarea
                    id="scheduleDescription"
                    value={formData.scheduleDescription || ''}
                    onChange={(e) => handleChange('scheduleDescription', e.target.value)}
                    placeholder="Descrição detalhada do agendamento..."
                    rows={2}
                  />
                </div>
                {formData.scheduleType === 'once' && (
                  <div className="form-group">
                    <label htmlFor="datetime">Data e Hora</label>
                    <input
                      type="datetime-local"
                      id="datetime"
                      value={formData.datetime || ''}
                      onChange={(e) => handleChange('datetime', e.target.value)}
                    />
                  </div>
                )}
                {formData.scheduleType === 'daily' && (
                  <div className="form-group">
                    <label htmlFor="time">Horário</label>
                    <input
                      type="time"
                      id="time"
                      value={formData.time || '09:00'}
                      onChange={(e) => handleChange('time', e.target.value)}
                    />
                  </div>
                )}
                {formData.scheduleType === 'weekly' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="dayOfWeek">Dia da Semana</label>
                      <select
                        id="dayOfWeek"
                        value={formData.dayOfWeek || '1'}
                        onChange={(e) => handleChange('dayOfWeek', e.target.value)}
                      >
                        <option value="0">Domingo</option>
                        <option value="1">Segunda-feira</option>
                        <option value="2">Terça-feira</option>
                        <option value="3">Quarta-feira</option>
                        <option value="4">Quinta-feira</option>
                        <option value="5">Sexta-feira</option>
                        <option value="6">Sábado</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="time">Horário</label>
                      <input
                        type="time"
                        id="time"
                        value={formData.time || '09:00'}
                        onChange={(e) => handleChange('time', e.target.value)}
                      />
                    </div>
                  </>
                )}
                {formData.scheduleType === 'monthly' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="dayOfMonth">Dia do Mês</label>
                      <input
                        type="number"
                        id="dayOfMonth"
                        value={formData.dayOfMonth || 1}
                        onChange={(e) => handleChange('dayOfMonth', parseInt(e.target.value) || 1)}
                        min="1"
                        max="31"
                        placeholder="1"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="time">Horário</label>
                      <input
                        type="time"
                        id="time"
                        value={formData.time || '09:00'}
                        onChange={(e) => handleChange('time', e.target.value)}
                      />
                    </div>
                  </>
                )}
                {formData.scheduleType === 'custom' && (
                  <div className="form-group">
                    <label htmlFor="cronExpression">Expressão Cron</label>
                    <input
                      type="text"
                      id="cronExpression"
                      value={formData.cronExpression || '0 9 * * *'}
                      onChange={(e) => handleChange('cronExpression', e.target.value)}
                      placeholder="0 9 * * *"
                    />
                    <small>Formato: minuto hora dia mês dia-da-semana</small>
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="entityType">Tipo de Entidade (opcional)</label>
                  <select
                    id="entityType"
                    value={formData.entityType || ''}
                    onChange={(e) => handleChange('entityType', e.target.value)}
                  >
                    <option value="">Nenhum</option>
                    <option value="checklist">Checklist</option>
                    <option value="manutencao">Manutenção</option>
                    <option value="veiculo">Veículo</option>
                    <option value="motorista">Motorista</option>
                  </select>
                </div>
                {formData.entityType && (
                  <div className="form-group">
                    <label htmlFor="entityId">ID da Entidade</label>
                    <input
                      type="text"
                      id="entityId"
                      value={formData.entityId || ''}
                      onChange={(e) => handleChange('entityId', e.target.value)}
                      placeholder="{{entity_id}} ou ID fixo"
                    />
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="notifyBefore">Notificar Antes (minutos)</label>
                  <input
                    type="number"
                    id="notifyBefore"
                    value={formData.notifyBefore || 0}
                    onChange={(e) => handleChange('notifyBefore', parseInt(e.target.value) || 0)}
                    min="0"
                    placeholder="0"
                  />
                  <small>0 = notificar no momento do evento</small>
                </div>
              </>
            )}

            {/* Atualizar / Cancelar / Completar */}
            {(formData.operation === 'update' || formData.operation === 'cancel' || formData.operation === 'complete') && (
              <div className="form-group">
                <label htmlFor="scheduleId">ID do Agendamento</label>
                <input
                  type="text"
                  id="scheduleId"
                  value={formData.scheduleId || ''}
                  onChange={(e) => handleChange('scheduleId', e.target.value)}
                  placeholder="{{schedule_id}} ou ID fixo"
                />
              </div>
            )}

            {/* Atualizar campos adicionais */}
            {formData.operation === 'update' && (
              <>
                <div className="form-group">
                  <label htmlFor="newDatetime">Nova Data/Hora (opcional)</label>
                  <input
                    type="datetime-local"
                    id="newDatetime"
                    value={formData.newDatetime || ''}
                    onChange={(e) => handleChange('newDatetime', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="newTitle">Novo Título (opcional)</label>
                  <input
                    type="text"
                    id="newTitle"
                    value={formData.newTitle || ''}
                    onChange={(e) => handleChange('newTitle', e.target.value)}
                    placeholder="Deixe vazio para manter"
                  />
                </div>
              </>
            )}

            {/* Listar e Verificar Pendentes */}
            {(formData.operation === 'list' || formData.operation === 'check_due') && (
              <>
                <div className="form-group">
                  <label htmlFor="filterStatus">Filtrar por Status</label>
                  <select
                    id="filterStatus"
                    value={formData.filterStatus || ''}
                    onChange={(e) => handleChange('filterStatus', e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="pending">Pendentes</option>
                    <option value="completed">Concluídos</option>
                    <option value="cancelled">Cancelados</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="filterEntityType">Filtrar por Tipo de Entidade</label>
                  <select
                    id="filterEntityType"
                    value={formData.filterEntityType || ''}
                    onChange={(e) => handleChange('filterEntityType', e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="checklist">Checklist</option>
                    <option value="manutencao">Manutenção</option>
                    <option value="veiculo">Veículo</option>
                    <option value="motorista">Motorista</option>
                  </select>
                </div>
              </>
            )}

            {formData.operation === 'check_due' && (
              <div className="form-group">
                <label htmlFor="dueInMinutes">Vencendo em (minutos)</label>
                <input
                  type="number"
                  id="dueInMinutes"
                  value={formData.dueInMinutes || 60}
                  onChange={(e) => handleChange('dueInMinutes', parseInt(e.target.value) || 60)}
                  min="1"
                  placeholder="60"
                />
                <small>Buscar agendamentos que vencem nos próximos X minutos</small>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="variable">Variável de Resultado</label>
              <input
                type="text"
                id="variable"
                value={formData.variable || 'schedule_result'}
                onChange={(e) => handleChange('variable', e.target.value)}
                placeholder="schedule_result"
              />
              <small>Nome da variável para armazenar o resultado</small>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva a operação de agendamento..."
                rows={2}
              />
            </div>
          </>
        );

      case 'switch':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do switch"
              />
            </div>
            <div className="form-group">
              <label htmlFor="variable">Variável a Avaliar</label>
              <input
                type="text"
                id="variable"
                value={formData.variable || ''}
                onChange={(e) => handleChange('variable', e.target.value)}
                placeholder="nome_variavel"
              />
              <small>Nome da variável que será avaliada</small>
            </div>
            <div className="form-group">
              <label htmlFor="mode">Modo de Comparação</label>
              <select
                id="mode"
                value={formData.mode || 'value'}
                onChange={(e) => handleChange('mode', e.target.value)}
              >
                <option value="value">Valor Exato</option>
                <option value="condition">Condição</option>
                <option value="regex">Expressão Regular</option>
              </select>
            </div>
            <div className="form-group">
              <label>Casos</label>
              <div className="switch-cases-editor">
                {(formData.cases || []).map((caseItem: any, index: number) => (
                  <div key={caseItem.id || index} className="switch-case-item">
                    <input
                      type="text"
                      value={caseItem.label || ''}
                      onChange={(e) => {
                        const newCases = [...(formData.cases || [])];
                        newCases[index] = { ...newCases[index], label: e.target.value };
                        handleChange('cases', newCases);
                      }}
                      placeholder="Nome do caso"
                      className="case-label-input"
                    />
                    <input
                      type="text"
                      value={caseItem.value || ''}
                      onChange={(e) => {
                        const newCases = [...(formData.cases || [])];
                        newCases[index] = { ...newCases[index], value: e.target.value };
                        handleChange('cases', newCases);
                      }}
                      placeholder="Valor/Condição"
                      className="case-value-input"
                    />
                    {formData.mode === 'condition' && (
                      <select
                        value={caseItem.operator || 'equals'}
                        onChange={(e) => {
                          const newCases = [...(formData.cases || [])];
                          newCases[index] = { ...newCases[index], operator: e.target.value };
                          handleChange('cases', newCases);
                        }}
                        className="case-operator-select"
                      >
                        <option value="equals">Igual</option>
                        <option value="not_equals">Diferente</option>
                        <option value="greater">Maior que</option>
                        <option value="less">Menor que</option>
                        <option value="contains">Contém</option>
                        <option value="starts_with">Começa com</option>
                        <option value="ends_with">Termina com</option>
                      </select>
                    )}
                    <button
                      type="button"
                      className="btn-remove-case"
                      onClick={() => {
                        const newCases = (formData.cases || []).filter((_: any, i: number) => i !== index);
                        handleChange('cases', newCases);
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-add-case"
                  onClick={() => {
                    const newCases = [
                      ...(formData.cases || []),
                      { id: `case-${Date.now()}`, label: '', value: '', operator: 'equals' }
                    ];
                    handleChange('cases', newCases);
                  }}
                >
                  <i className="fas fa-plus"></i> Adicionar Caso
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.defaultCase !== false}
                  onChange={(e) => handleChange('defaultCase', e.target.checked)}
                />
                <span>Incluir caso "Default" (quando nenhum outro corresponder)</span>
              </label>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva o switch..."
                rows={2}
              />
            </div>
          </>
        );

      case 'set_variables':
        return (
          <>
            <div className="form-group">
              <label htmlFor="label">Rótulo</label>
              <input
                type="text"
                id="label"
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Nome do nó"
              />
            </div>
            <div className="form-group">
              <label>Variáveis</label>
              <div className="variable-assignments-editor">
                {(formData.assignments || []).map((assignment: any, index: number) => (
                  <div key={assignment.id || index} className="variable-assignment-item">
                    <input
                      type="text"
                      value={assignment.name || ''}
                      onChange={(e) => {
                        const newAssignments = [...(formData.assignments || [])];
                        newAssignments[index] = { ...newAssignments[index], name: e.target.value };
                        handleChange('assignments', newAssignments);
                      }}
                      placeholder="nome_variavel"
                      className="var-name-input"
                    />
                    <span className="assignment-equals">=</span>
                    <input
                      type="text"
                      value={assignment.value || ''}
                      onChange={(e) => {
                        const newAssignments = [...(formData.assignments || [])];
                        newAssignments[index] = { ...newAssignments[index], value: e.target.value };
                        handleChange('assignments', newAssignments);
                      }}
                      placeholder="valor ou {{variavel}}"
                      className="var-value-input"
                    />
                    <select
                      value={assignment.type || 'string'}
                      onChange={(e) => {
                        const newAssignments = [...(formData.assignments || [])];
                        newAssignments[index] = { ...newAssignments[index], type: e.target.value };
                        handleChange('assignments', newAssignments);
                      }}
                      className="var-type-select"
                    >
                      <option value="string">Texto</option>
                      <option value="number">Número</option>
                      <option value="boolean">Booleano</option>
                      <option value="json">JSON</option>
                      <option value="expression">Expressão</option>
                      <option value="array">Array</option>
                    </select>
                    <button
                      type="button"
                      className="btn-remove-var"
                      onClick={() => {
                        const newAssignments = (formData.assignments || []).filter((_: any, i: number) => i !== index);
                        handleChange('assignments', newAssignments);
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-add-var"
                  onClick={() => {
                    const newAssignments = [
                      ...(formData.assignments || []),
                      { id: `var-${Date.now()}`, name: '', value: '', type: 'string' }
                    ];
                    handleChange('assignments', newAssignments);
                  }}
                >
                  <i className="fas fa-plus"></i> Adicionar Variável
                </button>
              </div>
              <small>Use {'{'}{'{'} variavel {'}'}{'}'}  para referenciar outras variáveis</small>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.keepOnlySet || false}
                  onChange={(e) => handleChange('keepOnlySet', e.target.checked)}
                />
                <span>Manter apenas as variáveis definidas (limpar outras)</span>
              </label>
            </div>
            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descreva as variáveis definidas..."
                rows={2}
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  // Contar ações configuradas
  const actionsCount = (formData.actions?.input?.length || 0) + (formData.actions?.output?.length || 0);

  return (
    <ModalPortal isOpen={isOpen}>
      <div className={`node-editor-modal ${isMobile ? 'mobile' : ''}`}>
        <div className="modal-overlay" onClick={onClose}></div>
        <div className="modal-content">
        <div className="modal-header">
          <h2>
            <i className="fas fa-edit"></i> Editar Nó
          </h2>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="modal-tabs">
          <button
            className={`modal-tab ${activeTab === 'config' ? 'active' : ''}`}
            onClick={() => setActiveTab('config')}
          >
            <i className="fas fa-cog"></i>
            <span>Configurações</span>
          </button>
          <button
            className={`modal-tab ${activeTab === 'actions' ? 'active' : ''}`}
            onClick={() => setActiveTab('actions')}
          >
            <i className="fas fa-bolt"></i>
            <span>Ações</span>
            {actionsCount > 0 && (
              <span className="tab-badge">{actionsCount}</span>
            )}
          </button>
        </div>

        <div className="modal-body">
          <div className="node-type-indicator">
            <span className={`type-badge ${node.type}`}>
              {node.type.toUpperCase()}
            </span>
          </div>

          {activeTab === 'config' ? (
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
              {renderFields()}
            </form>
          ) : (
            <div className="actions-tab-content">
              <NodeActionsEditor
                actions={formData.actions || { input: [], output: [] }}
                onChange={handleActionsChange}
              />
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            <i className="fas fa-check"></i> Salvar
          </button>
        </div>
      </div>
      </div>
    </ModalPortal>
  );
};

export default NodeEditorModal;
